import { GroupBase, OptionsOrGroups, Select } from "chakra-react-select";
import { useController } from "react-hook-form";
import apiClient from "services/ApiClient";
import { DebouncedFunc, debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { SystemStyleObject } from "@chakra-ui/react";
import { ControlledSelectProps, MENU_PORTAL_Z_INDEX, OptionType } from ".";

export interface GenericAsyncMultiSelectProps<T> extends ControlledSelectProps {
  embedOption: (arg0: T) => Option<T>;
  extractValue: (arg0: T) => string;
  fetchPath: string;
  excludedIds?: string[];
  onChangeCallback?: (arg0: T | undefined) => void;
  cacheKey?: string;
  readOnly?: boolean;
  includeDropdownIndicator?: boolean;
  containerStyles?: SystemStyleObject;
  onConfirm?: () => void;
  components?: {
    [key: string]: React.ComponentType<any>;
  };
  onMenuClose?: () => void;
  maxSelections?: number;
  resolveInitialValue?: (id: string) => Promise<Option<T> | undefined>;
  perPagePagination?: number;
}

interface Option<T> {
  value: string;
  label: string;
  rawValue: T;
}

async function fetchOptions<T>(
  path: string,
  extractValue: (arg0: T) => string,
  embedOption: (arg0: T) => Option<T>,
  inputValue: string,
  excludedIds: string[],
  perPagePagination?: number,
): Promise<OptionsOrGroups<Option<T>, GroupBase<Option<T>>>> {
  const separator = path.includes("?") ? "&" : "?";
  const pagination = perPagePagination ? `&pagination[per_page]=${perPagePagination}` : "";
  return apiClient
    .get<T[]>(`${path}${separator}q=${encodeURIComponent(inputValue)}${pagination}`)
    .then((value) => value.filter((v) => !(excludedIds || []).includes(extractValue(v))).map((v) => embedOption(v)))
    .catch(() => []);
}

export function GenericAsyncMultiSelect<T>({
  control,
  name,
  id,
  placeholder,
  embedOption,
  extractValue,
  rules,
  fetchPath,
  excludedIds = [],
  onChangeCallback = undefined,
  cacheKey = undefined,
  readOnly = false,
  variant,
  includeDropdownIndicator = false,
  className,
  containerStyles = {},
  components = {},
  onConfirm = undefined,
  onMenuClose = undefined,
  maxSelections = undefined,
  resolveInitialValue = undefined,
  perPagePagination = undefined,
  ...props
}: GenericAsyncMultiSelectProps<T>) {
  const {
    field: { onChange, value: controlValue },
  } = useController({
    name,
    control,
    rules,
  });
  type OptionsGroups = OptionsOrGroups<Option<T>, GroupBase<Option<T>>>;

  // When maxSelections=1, the form stores a single string (not an array).
  // Internally we normalize to an array for the multi-select UI.
  const isSingleMode = maxSelections === 1;
  const controlValueArray = useMemo(() => {
    if (isSingleMode) {
      return controlValue ? [controlValue] : [];
    }
    if (Array.isArray(controlValue)) return controlValue;
    return controlValue ? [controlValue] : [];
  }, [controlValue, isSingleMode]);

  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<OptionsGroups>([]);
  const [optionsCache, setOptionsCache] = useState<Record<string, OptionsGroups>>({});
  const [resolvedExtras, setResolvedExtras] = useState<Option<T>[]>([]);
  const deb = useRef<DebouncedFunc<() => Promise<void>> | undefined>();

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: OptionsGroups) => void) => {
      deb.current?.cancel();
      deb.current = debounce(async () => {
        setIsLoading(true);
        callback(
          await fetchOptions(fetchPath, extractValue, embedOption, inputValue, excludedIds || [], perPagePagination),
        );
        setIsLoading(false);
      }, 300);
      deb.current();
    },
    [fetchPath, extractValue, embedOption, excludedIds, perPagePagination],
  );

  useEffect(() => {
    if (optionsCache[""]) return;

    if (!resolveInitialValue) {
      loadOptions("", (opts) => {
        setOptions(opts);
        setOptionsCache(opts ? { ...optionsCache, "": opts } : optionsCache);
      });
    } else {
      loadOptions("", async (defaultOptions) => {
        let mergedOptions = [...defaultOptions];
        const values = controlValueArray;

        const unresolvedValues = values.filter(
          (v: string) => !defaultOptions.some((opt) => (opt as Option<T>).value === v),
        );

        const resolvedOptions = await Promise.all(
          unresolvedValues.map((v: string) => resolveInitialValue(v).catch(() => undefined)),
        );

        resolvedOptions.forEach((resolved) => {
          if (resolved) mergedOptions = [resolved, ...mergedOptions];
        });

        setOptions(mergedOptions);
        setOptionsCache((prev) => ({ ...prev, "": mergedOptions }));
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onInputChange = useCallback(
    (newValue: string) => {
      if (!newValue) {
        setOptions(optionsCache[""] || []);
        return;
      }
      if (optionsCache[newValue]) {
        setOptions(optionsCache[newValue]);
      } else {
        loadOptions(newValue, (opts) => {
          setOptions(opts || []);
          setOptionsCache(opts ? { ...optionsCache, [newValue]: opts } : optionsCache);
        });
      }
    },
    [loadOptions, optionsCache],
  );

  const handleOnChange = useCallback(
    (val: OptionType<T>[] | undefined) => {
      let selected = val || [];
      if (maxSelections && selected.length > maxSelections) {
        selected = selected.slice(-maxSelections);
      }
      if (isSingleMode) {
        onChange(selected[0]?.value || "");
        if (onChangeCallback) onChangeCallback(selected[0]?.rawValue);
      } else {
        onChange(selected.map((option) => option.value));
        if (onChangeCallback) onChangeCallback(selected.map((option) => option.rawValue));
      }
    },
    [onChange, onChangeCallback, maxSelections, isSingleMode],
  );

  // Resolve values that arrive after mount (e.g., via form.reset)
  useEffect(() => {
    if (!resolveInitialValue || controlValueArray.length === 0) return;

    const allKnown = [...options, ...resolvedExtras];
    const unresolvedValues = controlValueArray.filter(
      (v: string) => !allKnown.some((opt) => (opt as Option<T>).value === v),
    );

    if (unresolvedValues.length === 0) return;

    let cancelled = false;
    Promise.all(unresolvedValues.map((v: string) => resolveInitialValue(v).catch(() => undefined))).then((resolved) => {
      if (cancelled) return;
      const newOptions = resolved.filter(Boolean) as Option<T>[];
      if (newOptions.length > 0) {
        setResolvedExtras((prev) => {
          const existingValues = new Set(prev.map((o) => o.value));
          const unique = newOptions.filter((o) => !existingValues.has(o.value));
          return unique.length > 0 ? [...unique, ...prev] : prev;
        });
        setOptionsCache((prev) => {
          const cached = prev[""] || [];
          const deduplicated = newOptions.filter((opt) => !cached.some((c) => (c as Option<T>).value === opt.value));
          return deduplicated.length > 0 ? { ...prev, "": [...deduplicated, ...cached] } : prev;
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [controlValueArray]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive selected options from controlValue + available options (no useEffect needed)
  const value = useMemo(() => {
    if (controlValueArray.length === 0) return [];
    const allKnown = [...options, ...resolvedExtras];
    const seen = new Set<string>();
    return allKnown.filter((option) => {
      const val = (option as Option<T>).value;
      if (seen.has(val) || !controlValueArray.includes(val)) return false;
      seen.add(val);
      return true;
    }) as Option<T>[];
  }, [controlValueArray, options, resolvedExtras]);
  const formVariant = variant;
  const singleModeHasValue = isSingleMode && value.length > 0;

  return (
    <Select
      key={cacheKey}
      name={name}
      isLoading={isLoading}
      menuPortalTarget={document.body}
      useBasicStyles
      isMulti
      isClearable={!readOnly}
      placeholder={formVariant === "floating" ? " " : placeholder}
      onChange={handleOnChange}
      options={options}
      value={value}
      onInputChange={onInputChange}
      components={components || (includeDropdownIndicator ? {} : { DropdownIndicator: null })}
      classNamePrefix="chakra-react-select"
      className={clsx("chakra-react-select-container-multi-select", className)}
      variant={formVariant}
      onMenuClose={onMenuClose}
      {...(onConfirm ? { onConfirm } : {})}
      chakraStyles={{
        container: (provided) => ({
          ...provided,
          cursor: "pointer",
          ...containerStyles,
          ...(singleModeHasValue ? { cursor: "default" } : {}),
        }),
        // Decouple menu width from the trigger so options like
        // "Alonzo Kertzmann — meryl_hermann@example.com" stay readable when
        // the picker is embedded in a narrow column.
        menu: (provided) => ({
          ...provided,
          minWidth: "320px",
          width: "max-content",
          maxWidth: "min(480px, 90vw)",
        }),
        option: (provided) => ({
          ...provided,
          color: "fg.muted",
        }),
        valueContainer: (provided) => ({
          ...provided,
          className: "multi-select",
        }),
        multiValueLabel: (provided) => ({
          ...provided,
          color: "fg.default",
        }),
        multiValue: (provided) => ({
          ...provided,
          background: "multiSelectBlue.DEFAULT",
        }),
        multiValueRemove: (provided) => ({
          ...provided,
          color: "fg.default",
          fontSize: "sm",
          fontWeigth: "bold",
          opacity: "0.7",
          "&hover": {
            opacity: "1",
          },
        }),
      }}
      styles={{ menuPortal: (provided) => ({ ...provided, zIndex: MENU_PORTAL_Z_INDEX }) }}
      {...props}
      {...(singleModeHasValue || readOnly ? { menuIsOpen: false, isSearchable: false } : {})}
    />
  );
}
